/**
 * หน้าเข้าใช้งาน — เด็กใส่รหัสครอบครัวแล้วเลือกโปรไฟล์ตัวเอง · ผู้ปกครองใช้อีเมล
 *
 * ★ เด็ก 12 ขวบต้องเข้าเองได้โดยไม่มีผู้ใหญ่ข้าง ๆ จึงต้อง:
 *   - จำรหัสครอบครัวไว้ ไม่ต้องพิมพ์ใหม่ทุกครั้ง
 *   - เลือกจากรายชื่อพร้อมอวตาร ไม่ใช่พิมพ์ชื่อ
 *   - แป้น PIN เป็นปุ่มใหญ่ ไม่ใช่ช่องพิมพ์ (ไม่ต้องสู้กับคีย์บอร์ดของ iPad)
 *   - ใส่ผิดแล้วบอกตรง ๆ ว่าเหลืออีกกี่ครั้ง ไม่ใช่ปล่อยให้โดนล็อกโดยไม่รู้ตัว
 *
 * ⚠️ PDPA — รายชื่อจาก `/auth/learner-directory` เปิดสาธารณะ จึงมีแค่
 *   ชื่อเล่น อวตาร ระดับชั้น เท่านั้น และเราไม่เก็บชื่อเล่นลง localStorage
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Shell } from "@/components/Shell";
import { AVATARS } from "@/lib/avatars";
import { translate as t } from "@/i18n";
import { ApiError, api } from "@/lib/api";
import {
  forgetOrgCode,
  rememberOrgCode,
  rememberedOrgCode,
  useSession,
} from "@/lib/session";
import type {
  AuthTokenResponse,
  LearnerBrief,
  LearnerDirectory,
} from "@/lib/types";

type Step = "choose" | "org" | "kids" | "pin" | "parent";

export function GatePage(): JSX.Element {
  const navigate = useNavigate();
  const adopt = useSession((s) => s.adopt);

  const [step, setStep] = useState<Step>("choose");
  const [orgCode, setOrgCode] = useState(rememberedOrgCode());
  const [kids, setKids] = useState<LearnerBrief[]>([]);
  const [kid, setKid] = useState<LearnerBrief | null>(null);
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function loadKids(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const code = orgCode.trim().toUpperCase();
      const dir = await api.get<LearnerDirectory>(
        `/auth/learner-directory?org_code=${encodeURIComponent(code)}`,
      );
      rememberOrgCode(code);
      setKids(dir.learners);
      setStep("kids");
    } catch (e) {
      if (e instanceof ApiError && e.code === "NOT_FOUND") {
        // รหัสนี้ไม่มีแล้ว — อาจเพราะผู้ปกครองเพิ่งเปลี่ยน (D16) ลืมค่าที่จำไว้
        // แต่คงข้อความในช่องไว้ให้แก้คำผิดได้ ไม่ล้างจนต้องพิมพ์ใหม่หมด
        forgetOrgCode();
        setError(t("gate.orgCodeGone"));
      } else {
        setError(e instanceof ApiError ? e.message : t("error.network"));
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitPin(value: string): Promise<void> {
    if (!kid) return;
    setBusy(true);
    setError(null);
    try {
      const tokens = await api.post<AuthTokenResponse>("/auth/learner-login", {
        org_code: orgCode.trim().toUpperCase(),
        learner_id: kid.id,
        pin: value,
      });
      await adopt(tokens);
      navigate("/learn", { replace: true });
    } catch (e) {
      setPin("");
      if (e instanceof ApiError && e.code === "INVALID_PIN") {
        const left = Number(
          (e.details as { attempts_remaining?: number })?.attempts_remaining ??
            0,
        );
        setError(t("gate.wrongPin", "th", { left }));
      } else if (e instanceof ApiError && e.code === "PIN_LOCKED") {
        const seconds = Number(
          (e.details as { retry_after_seconds?: number })
            ?.retry_after_seconds ?? 300,
        );
        setError(t("gate.locked", "th", { minutes: Math.ceil(seconds / 60) }));
      } else {
        setError(e instanceof ApiError ? e.message : t("error.network"));
      }
    } finally {
      setBusy(false);
    }
  }

  function tapKey(digit: string): void {
    if (busy) return;
    const next = (pin + digit).slice(0, 4);
    setPin(next);
    if (next.length === 4) void submitPin(next);
  }

  async function signInParent(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const tokens = await api.post<AuthTokenResponse>("/auth/login", {
        email,
        password,
      });
      await adopt(tokens);
      navigate("/parent", { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t("error.network"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      <div className="gate">
        {step === "choose" ? (
          <>
            <h1>{t("gate.title")}</h1>
            <p className="sub">{t("gate.subtitle")}</p>
            <button type="button" className="go" onClick={() => setStep("org")}>
              {t("gate.asLearner")}
            </button>
            <button
              type="button"
              className="link"
              style={{ display: "block", margin: "18px auto 0" }}
              onClick={() => setStep("parent")}
            >
              {t("gate.asParent")}
            </button>
            <button
              type="button"
              className="link"
              style={{ display: "block", margin: "0 auto" }}
              onClick={() => navigate("/register")}
            >
              {t("gate.register")}
            </button>
          </>
        ) : null}

        {step === "org" ? (
          <>
            <h1>{t("gate.asLearner")}</h1>
            {error ? <p className="err">{error}</p> : null}
            <div className="field">
              <label htmlFor="org">{t("gate.orgCode")}</label>
              <input
                id="org"
                value={orgCode}
                onChange={(e) => setOrgCode(e.target.value)}
                placeholder="TWP-0000"
                maxLength={14}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void loadKids();
                }}
              />
              <span style={{ fontSize: 13, color: "var(--ink-3)" }}>
                {t("gate.orgCodeHint")}
              </span>
            </div>
            <button
              type="button"
              className="go"
              disabled={busy}
              onClick={() => void loadKids()}
            >
              {t("gate.findKids")}
            </button>
          </>
        ) : null}

        {step === "kids" ? (
          <>
            <h1>{t("gate.pickKid")}</h1>
            {error ? <p className="err">{error}</p> : null}
            <div className="kids">
              {kids.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  className="kid"
                  disabled={!k.has_pin}
                  title={k.has_pin ? undefined : t("gate.noPin")}
                  onClick={() => {
                    setKid(k);
                    setPin("");
                    setError(null);
                    setStep("pin");
                  }}
                >
                  <span className="face">{AVATARS[k.avatar] ?? "🙂"}</span>
                  <b>{k.nickname}</b>
                  <i>{k.grade_label}</i>
                </button>
              ))}
            </div>
            <button
              type="button"
              className="link"
              onClick={() => setStep("org")}
            >
              {t("nav.back")}
            </button>
          </>
        ) : null}

        {step === "pin" && kid ? (
          <>
            <h1>{t("gate.pinTitle")}</h1>
            <p className="sub">
              {t("gate.pinFor", "th", { name: kid.nickname })}
            </p>
            {error ? <p className="err">{error}</p> : null}
            <div className="pinrow">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className={`pindot${i < pin.length ? " on" : ""}`}
                />
              ))}
            </div>
            <div className="keys">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
                <button
                  key={d}
                  type="button"
                  className="key"
                  onClick={() => tapKey(d)}
                >
                  {d}
                </button>
              ))}
              <button
                type="button"
                className="key ghost"
                onClick={() => {
                  setKid(null);
                  setPin("");
                  setStep("kids");
                }}
              >
                {t("nav.back")}
              </button>
              <button type="button" className="key" onClick={() => tapKey("0")}>
                0
              </button>
              <button
                type="button"
                className="key ghost"
                onClick={() => setPin((p) => p.slice(0, -1))}
              >
                {t("gate.delete")}
              </button>
            </div>
          </>
        ) : null}

        {step === "parent" ? (
          <>
            <h1>{t("gate.asParent")}</h1>
            {error ? <p className="err">{error}</p> : null}
            <div className="field">
              <label htmlFor="email">{t("gate.email")}</label>
              <input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="pw">{t("gate.password")}</label>
              <input
                id="pw"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void signInParent();
                }}
              />
            </div>
            <button
              type="button"
              className="go"
              disabled={busy}
              onClick={() => void signInParent()}
            >
              {t("gate.signIn")}
            </button>
            <button
              type="button"
              className="link"
              onClick={() => navigate("/register")}
            >
              {t("gate.register")}
            </button>
            <button
              type="button"
              className="link"
              onClick={() => setStep("choose")}
            >
              {t("nav.back")}
            </button>
          </>
        ) : null}
      </div>
    </Shell>
  );
}
